import { Pencil } from "lucide-react";
import { useRef, useState } from "react";

import { showError } from "@/lib/toast";

/**
 * その場で編集できるセル（インライン編集）。
 *
 * 値はクリック可能なテキストで見せ、クリックすると**セルの中が入力欄に変わる**。
 * Enter か入力欄から離れたときに保存し、Esc で取り消す。ダイアログを開かずに
 * 数量・単価・摘要のような独立した項目を直せるようにするためのもの。
 *
 * 他の項目と影響し合う項目（見積明細の図番と品名のように、片方を決めると
 * もう片方が決まるもの）はここでは扱わない。1 項目だけ先に保存できてしまうと、
 * 途中の矛盾した状態を作れてしまうため、そちらはダイアログで一括して編集する。
 *
 * 保存の失敗はトーストで知らせ、値は元に戻す。セルは幅が狭くエラー文言を
 * 置けないため。
 *
 * `type="date"` はブラウザの日付入力をそのままセルに出す。日付は打ち間違いが
 * 起きやすく、カレンダーから選べたほうが速いため。
 *
 * **値があるときも鉛筆アイコンを添える。** 青字＋下線のクリックできる値には
 * 「別の画面へ移る」ものもあり（[[LinkCell]]）、印が無いと押すまでどちらか
 * 分からない。鉛筆＝その場で直せる、右上向きの矢印＝画面が移る、で見分ける。
 */
export default function InlineEditCell({
  label,
  value,
  displayValue,
  canWrite,
  type = "text",
  numeric = false,
  onSave,
}: {
  /** 項目名。読み上げ用のラベルと、失敗時のメッセージに使う。 */
  label: string;
  /** 編集時の初期値。保存するかどうかの判定にも使う（生の値）。 */
  value: string | null | undefined;
  /** 表示だけ別にしたいとき（金額の 3 桁区切りなど）。省略すると value を出す。 */
  displayValue?: string | null;
  canWrite: boolean;
  type?: "text" | "number" | "date";
  /** 数値列（右寄せにする）。 */
  numeric?: boolean;
  /** 保存する。エラー文言を返すと値を元に戻す。 */
  onSave: (value: string | null) => Promise<string | null>;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  // Enter で確定したあとの blur で二重に保存しないようにする。
  const committed = useRef(false);

  const original = value?.toString().trim() ?? "";
  const display = (displayValue ?? original) || null;

  const start = () => {
    setText(original);
    committed.current = false;
    setEditing(true);
  };

  const cancel = () => {
    committed.current = true;
    setEditing(false);
  };

  const commit = async () => {
    if (committed.current) return;
    committed.current = true;

    const next = text.trim();
    if (next === original) {
      setEditing(false);
      return;
    }

    setSaving(true);
    const message = await onSave(next || null);
    setSaving(false);
    setEditing(false);
    if (message) showError(`${label}を保存できませんでした: ${message}`);
  };

  if (editing) {
    return (
      <input
        autoFocus
        type={type}
        aria-label={`${label}を入力`}
        value={text}
        disabled={saving}
        onChange={(e) => setText(e.target.value)}
        onFocus={(e) => e.target.select()}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void commit();
          }
          if (e.key === "Escape") {
            e.preventDefault();
            cancel();
          }
        }}
        className={[
          "w-full rounded border border-brand-500 bg-white px-1 py-0.5 text-sm outline-none",
          numeric ? "text-right" : "",
        ].join(" ")}
      />
    );
  }

  if (!canWrite) {
    return display ? (
      <span>{display}</span>
    ) : (
      <span className="text-default-400">—</span>
    );
  }

  if (!display) {
    return (
      <button
        type="button"
        onClick={start}
        aria-label={`${label}を編集`}
        className={[
          "inline-flex cursor-pointer items-center gap-1 rounded px-1 py-0.5 hover:bg-default-100",
          numeric ? "justify-end" : "",
        ].join(" ")}
      >
        <span className="text-default-400">—</span>
        <Pencil size={12} className="text-brand-500" />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={start}
      aria-label={`${label}を編集`}
      className={[
        "inline-flex max-w-full cursor-pointer items-center gap-1 rounded px-1 py-0.5 text-brand-500 hover:text-brand-600",
        // 数値列は右寄せなので、セル幅いっぱいに広げて中身を右へ寄せる。
        numeric ? "w-full justify-end" : "",
      ].join(" ")}
    >
      <span className="truncate underline underline-offset-2">
        {saving ? "保存中..." : display}
      </span>
      <Pencil size={12} className="shrink-0" aria-hidden />
    </button>
  );
}
